import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PencilRuler, Cog, Wrench, Cpu, CheckCircle2 } from "lucide-react";

const DISCIPLINES = [
  "Mechanical Design",
  "Electrical Design",
  "Automation & Controls",
  "Machining",
  "Welding & Fabrication",
  "Steel · Stainless · Aluminium",
  "Control Cabinet Build",
  "PLC Programming",
  "HMI Programming",
  "Install & Commissioning",
];

const STORY = [
  {
    icon: PencilRuler,
    label: "01 — Design",
    title: "Mechanical, electrical & control",
    body: "Customer brief converted into a full mechanical, electrical and control system design — tangential head geometry, drive sizing, safety interlocks and HMI flow.",
  },
  {
    icon: Cog,
    label: "02 — Machining",
    title: "Precision-machined assemblies",
    body: "Drive components, tape-head spindles and bearing carriers machined in-house to tight tolerances.",
  },
  {
    icon: Wrench,
    label: "03 — Fabrication",
    title: "Frame, guards & enclosure",
    body: "Heavy-duty steel frame, machine guarding and bespoke enclosure fabricated, finished and assembled at our NW facility.",
  },
  {
    icon: Cpu,
    label: "04 — Cabinet, PLC & HMI",
    title: "Control cabinet built & programmed",
    body: "Control cabinet wired and tested in-house; Siemens PLC logic and HMI screens written and proven by Lutrom engineers.",
  },
  {
    icon: CheckCircle2,
    label: "05 — Install & Commission",
    title: "Running on the customer's line",
    body: "Delivered, installed, terminated, commissioned and handed over to the cable manufacturer — production-ready from day one.",
  },
];

const CaseStudies = () => {
  return (
    <section
      className="relative mt-24 lg:mt-32"
      data-testid="case-studies-section"
      id="case-studies"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <p className="eyebrow">Featured Project · Cable Manufacturing</p>
            <h2
              className="font-display mt-5 text-white"
              style={{ fontSize: "clamp(36px, 5vw, 60px)", lineHeight: 0.95 }}
            >
              BESPOKE TANGENTIAL
              <br />
              <span className="gradient-blue-text">TAPING MACHINE.</span>
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-[color:var(--lt-text-dim)]">
              A complete, in-house build for a UK cable manufacturer — mechanical
              and electrical design, machining, fabrication, control cabinet build,
              Siemens PLC and HMI programming, plus install and commissioning. One
              supplier. One accountable team. Zero subcontractors.
            </p>
          </div>
          <Link
            to="/services#bespoke-machinery"
            className="btn-ghost inline-flex w-fit items-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold"
            data-testid="case-studies-service-link"
          >
            See bespoke machinery service <ArrowRight size={15} />
          </Link>
        </div>

        {/* Discipline pills */}
        <div
          className="flex flex-wrap gap-2 mb-10"
          data-testid="case-study-disciplines"
        >
          {DISCIPLINES.map((d) => (
            <span
              key={d}
              className="px-3.5 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-semibold"
              style={{
                color: "var(--lt-text)",
                background: "rgba(15, 26, 51, 0.7)",
                border: "1px solid rgba(127, 166, 255, 0.35)",
              }}
            >
              {d}
            </span>
          ))}
        </div>

        {/* Hero: video + intro panel */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div
            className="lg:col-span-8 relative rounded-2xl overflow-hidden service-card p-0"
            data-testid="case-study-video-wrap"
          >
            <video
              src="/case-study/machine.mp4"
              poster="/case-study/machine-poster.jpg"
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              className="w-full h-full object-cover block"
              style={{ aspectRatio: "16 / 10", background: "#000" }}
              data-testid="case-study-video"
            />
            <div
              className="absolute inset-x-0 bottom-0 p-6 lg:p-8"
              style={{
                background:
                  "linear-gradient(180deg, rgba(6,11,24,0) 0%, rgba(6,11,24,0.85) 100%)",
              }}
            >
              <p className="eyebrow" style={{ color: "rgba(230,236,255,0.85)" }}>
                Live — tangential taping head in operation
              </p>
              <h3
                className="font-display mt-2 text-white"
                style={{ fontSize: "clamp(22px, 2.6vw, 32px)", lineHeight: 1.02 }}
              >
                TANGENTIAL TAPING MACHINE
              </h3>
              <p className="mt-2 text-sm text-[color:var(--lt-text-dim)] max-w-xl">
                Spinning tape head driving a continuous, tangential wrap over the
                cable — controlled via a Siemens PLC and Lutrom-programmed HMI.
              </p>
            </div>
          </div>

          <div className="lg:col-span-4 grid grid-cols-1 gap-5">
            <div className="glow-card rounded-2xl p-6">
              <p className="eyebrow" style={{ color: "rgba(230,236,255,0.9)" }}>
                Project outcome
              </p>
              <div className="mt-5 grid grid-cols-3 gap-3">
                {[
                  { v: "100%", l: "In-house scope" },
                  { v: "10", l: "Disciplines" },
                  { v: "0", l: "Subcontractors" },
                ].map((s) => (
                  <div
                    key={s.l}
                    className="rounded-lg p-3 text-center"
                    style={{
                      background: "rgba(6, 11, 24, 0.45)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <p
                      className="font-display text-white"
                      style={{ fontSize: 22, lineHeight: 1 }}
                    >
                      {s.v}
                    </p>
                    <p className="mt-1.5 text-[10px] uppercase tracking-widest text-[color:rgba(230,236,255,0.7)]">
                      {s.l}
                    </p>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-sm text-[color:rgba(230,236,255,0.78)] leading-relaxed">
                Design, machining, fabrication, control cabinet, PLC &amp; HMI
                programming, install and commissioning — every discipline delivered
                by Lutrom engineers.
              </p>
            </div>

            <div
              className="rounded-2xl overflow-hidden service-card p-0 relative"
              data-testid="case-study-internals"
            >
              <img
                src="/case-study/internals.jpeg"
                alt="Inside the tangential taping machine — Siemens HMI panel and Lutrom-branded mesh over the precision tangential tape head"
                className="w-full block"
                style={{ aspectRatio: "1 / 1", objectFit: "cover" }}
                loading="lazy"
              />
              <div
                className="absolute inset-x-0 bottom-0 p-5"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(6,11,24,0) 0%, rgba(6,11,24,0.85) 100%)",
                }}
              >
                <p className="eyebrow" style={{ color: "rgba(230,236,255,0.85)" }}>
                  Engineering detail
                </p>
                <p className="mt-2 text-sm text-white font-semibold">
                  Tangential tape head, Lutrom-built cabinet &amp; HMI.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Second photo — wide delivery shot */}
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-7 rounded-2xl overflow-hidden service-card p-0 relative">
            <img
              src="/case-study/delivery.jpeg"
              alt="Lutrom Engineering HQ at Unit 28, Winnington — completed tangential taping machine being lifted onto a flatbed for delivery to the cable manufacturer"
              className="w-full block"
              style={{ aspectRatio: "16 / 11", objectFit: "cover" }}
              loading="lazy"
              data-testid="case-study-delivery-img"
            />
            <div
              className="absolute inset-x-0 bottom-0 p-6 lg:p-8"
              style={{
                background:
                  "linear-gradient(180deg, rgba(6,11,24,0) 0%, rgba(6,11,24,0.85) 100%)",
              }}
            >
              <p className="eyebrow" style={{ color: "rgba(230,236,255,0.85)" }}>
                Leaving HQ — Unit 28
              </p>
              <p className="mt-2 text-sm text-white font-semibold">
                Lift plan, transport and on-site install at the cable manufacturer.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5 glass-card rounded-2xl p-6 lg:p-8">
            <p className="eyebrow">The end-to-end workflow</p>
            <h3
              className="font-display mt-4 text-white"
              style={{ fontSize: "clamp(24px, 2.8vw, 32px)", lineHeight: 1.02 }}
            >
              EVERY DISCIPLINE,
              <br />
              <span className="gradient-blue-text">UNDER ONE ROOF.</span>
            </h3>
            <ul className="mt-6 space-y-4">
              {STORY.map((s) => {
                const Icon = s.icon;
                return (
                  <li
                    key={s.label}
                    className="flex items-start gap-4"
                    data-testid={`case-study-step-${s.label.split(" ")[0]}`}
                  >
                    <span className="icon-tile" style={{ width: 40, height: 40, flexShrink: 0 }}>
                      <Icon size={18} />
                    </span>
                    <div>
                      <p className="text-[11px] uppercase tracking-widest text-[color:var(--lt-blue-soft)] font-semibold">
                        {s.label}
                      </p>
                      <p className="mt-1 font-display text-white" style={{ fontSize: 18 }}>
                        {s.title.toUpperCase()}
                      </p>
                      <p className="mt-1 text-[13px] text-[color:var(--lt-text-dim)] leading-relaxed">
                        {s.body}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-wrap justify-center gap-3">
          <Link
            to="/contact"
            className="btn-primary inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"
            data-testid="case-studies-cta-contact"
          >
            Discuss a bespoke project <ArrowRight size={15} />
          </Link>
          <Link
            to="/services#bespoke-machinery"
            className="btn-ghost inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"
            data-testid="case-studies-cta-services"
          >
            Bespoke machinery service
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
